<script setup lang="ts">
import { ampli } from '@pkg/analytics-helper/src/ampli';
import { logger } from '@pkg/logger';
import { Loader2 } from 'lucide-vue-next';
import type { MapData } from '../../types/mapTypes';
import { useStaticMapUrl } from '~/app/composables/useStaticMapUrl';

const props = defineProps<{
  marker: string;
  size: string;
  style: string;
  zoom: number;
  stylesClass: string;
  mapData?: MapData;
}>();

const finalUrl = ref('');
const isLoading = ref(true);
const mapError = ref(false);

const device = useDevice();
const { getStaticMapUrl } = useStaticMapUrl();

const onMapError = (event?: Event) => {
  const target = event?.target as HTMLImageElement;
  const src = target?.src;
  mapError.value = true;
  isLoading.value = false;
  logger.error('Map image failed to load', { src, event });
  logger.debug('Map image failed to load', { src, event });
};

const mapCoordinates = computed(() => {
  if (props.mapData?.coordinates?.latitude && props.mapData?.coordinates?.longitude) {
    return `${props.mapData?.coordinates.latitude},${props.mapData?.coordinates.longitude}`;
  }
  onMapError();
  return '';
});

const getMapUrl = async() => {
  if (!props.mapData?.coordinates) {
    logger.error('Coordinates are required');
    mapError.value = true;
    isLoading.value = false;
    return;
  }

  try {
    const url = await getStaticMapUrl({
      coordinates: mapCoordinates.value,
      zoom: props.zoom,
      size: props.size,
      marker: props.marker,
      style: props.style,
    });

    if (url) {
      finalUrl.value = url;
    } else {
      mapError.value = true;
    }
    isLoading.value = false;
  } catch (error) {
    isLoading.value = false;
    mapError.value = true;
    logger.error('Failed to generate map URL', error);
  }
};

const openInMaps = () => {
  if (!props.mapData?.coordinates) {
    return;
  }

  const { storeName, address } = props.mapData;
  const searchQuery = storeName && address
    ? `${storeName} + ${address}`
    : address || '';

  const urls = {
    web: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(searchQuery)}`,
    ios: `maps://maps.apple.com/?q=${encodeURIComponent(searchQuery)}`,
    android: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(searchQuery)}`,
  };

  let finalUrl = urls.web;

  if (device.isMobile) {
    if (device.isIos) {
      finalUrl = urls.ios;
    } else if (device.isAndroid) {
      finalUrl = urls.android;
    }
  }

  try {
    ampli.mapClick({
      item_page: 'map_static',
    });
  } catch (error) {
    logger.error('[LocationFinder] Amplitude error:', error);
  }

  window.open(finalUrl, '_blank');
};

onMounted(() => {
  getMapUrl();
});
</script>

<template>
  <div class="flex justify-center" :class="stylesClass">
    <Loader2 v-if="isLoading" class="s-map__loader animate-spin" />
    <img
      v-if="finalUrl && !isLoading && !mapError"
      :src="finalUrl"
      alt="Map"
      :class="['s-map-clickable', stylesClass]"
      @error="onMapError"
      @click="openInMaps"
    >
    <p v-if="mapError && mapData?.errorText" class="pt-32">
      {{ mapData?.errorText }}
    </p>
  </div>
</template>

<style scoped>
.s-map__loader {
  height: theme('spacing.32');
  width: theme('spacing.32');
  color: var(--theme-heading-color-app);
  margin: theme('spacing.40');

  @screen desk {
    margin: theme('spacing.96');
  }
}

.s-map-clickable {
  cursor: pointer;
  transition: opacity 0.2s ease;
  border-radius: 15px;
}

.s-map-clickable:hover {
  opacity: 0.9;
}
</style>
